import type { HomeFeedCard, HomeFeedStackSummary } from '@aikoa/common'
import { useCallback, useEffect, useState } from 'react'
import {
  useHomeFeedNextCard,
  useHomeFeedStackSummary,
  useMarkFeedStackCardSeen,
} from '~/data/feed/useFeed'

type FeedStackSession = {
  cutoffAt: string
  totalCount: number
  remainingCount: number
  leadCount: number
  assetCount: number
  currentCard: HomeFeedCard | null
  hasPagedThroughStack: boolean
  isInitialized: boolean
}

export type UseHomeFeedStackResult = {
  currentCard: HomeFeedCard | null
  remainingCount: number
  totalCount: number
  currentCardIndex: number
  leadCount: number
  assetCount: number
  hasPagedThroughStack: boolean
  isLoading: boolean
  isAdvancing: boolean
  dismissCard: (card: HomeFeedCard) => void
}

let persistedFeedStackSession: FeedStackSession | null = null

function createFeedStackSession(): FeedStackSession {
  return {
    cutoffAt: new Date().toISOString(),
    totalCount: 0,
    remainingCount: 0,
    leadCount: 0,
    assetCount: 0,
    currentCard: null,
    hasPagedThroughStack: false,
    isInitialized: false,
  }
}

function getOrCreateFeedStackSession() {
  if (persistedFeedStackSession) {
    return persistedFeedStackSession
  }

  const nextSession = createFeedStackSession()
  persistedFeedStackSession = nextSession

  return nextSession
}

function getNextSummaryAfterDismiss(
  summary: HomeFeedStackSummary,
  card: HomeFeedCard
): HomeFeedStackSummary {
  return {
    totalCount: Math.max(summary.totalCount - 1, 0),
    leadCount: Math.max(summary.leadCount - (card.kind.startsWith('lead_') ? 1 : 0), 0),
    assetCount: Math.max(summary.assetCount - (card.kind.startsWith('lead_') ? 0 : 1), 0),
  }
}

function syncSessionWithServerState(
  session: FeedStackSession,
  summary: HomeFeedStackSummary,
  card: HomeFeedCard | null
): FeedStackSession {
  const totalCount = session.hasPagedThroughStack ? session.totalCount : summary.totalCount
  const remainingCount = session.hasPagedThroughStack
    ? Math.min(session.remainingCount, summary.totalCount)
    : summary.totalCount
  const leadCount = session.hasPagedThroughStack
    ? Math.min(session.leadCount, summary.leadCount)
    : summary.leadCount
  const assetCount = session.hasPagedThroughStack
    ? Math.min(session.assetCount, summary.assetCount)
    : summary.assetCount

  return {
    ...session,
    totalCount,
    remainingCount,
    leadCount,
    assetCount,
    currentCard: card,
    isInitialized: true,
  }
}

export function useHomeFeedStack(): UseHomeFeedStackResult {
  const [session, setSession] = useState<FeedStackSession>(() => getOrCreateFeedStackSession())
  const markCardSeen = useMarkFeedStackCardSeen()
  const shouldHydrateSession = !session.isInitialized
  const shouldRecoverCard =
    session.isInitialized &&
    !markCardSeen.isPending &&
    session.currentCard === null &&
    session.remainingCount > 0
  const summaryQuery = useHomeFeedStackSummary(
    session.cutoffAt,
    shouldHydrateSession || shouldRecoverCard
  )
  const nextCardQuery = useHomeFeedNextCard(
    session.cutoffAt,
    shouldHydrateSession || shouldRecoverCard
  )

  useEffect(() => {
    persistedFeedStackSession = session
  }, [session])

  useEffect(() => {
    const summary = summaryQuery.data

    if (!summary || !shouldHydrateSession) {
      return
    }

    if (summary.totalCount > 0 && nextCardQuery.data === undefined) {
      return
    }

    setSession((currentSession) => {
      if (currentSession.isInitialized) {
        return currentSession
      }

      const nextSession = syncSessionWithServerState(
        currentSession,
        summary,
        nextCardQuery.data ?? null
      )

      persistedFeedStackSession = nextSession

      return nextSession
    })
  }, [nextCardQuery.data, shouldHydrateSession, summaryQuery.data])

  useEffect(() => {
    const summary = summaryQuery.data

    if (!summary || !shouldRecoverCard || nextCardQuery.data === undefined) {
      return
    }

    setSession((currentSession) => {
      if (currentSession.currentCard || currentSession.remainingCount === 0) {
        return currentSession
      }

      const nextSession = syncSessionWithServerState(
        currentSession,
        summary,
        nextCardQuery.data ?? null
      )

      persistedFeedStackSession = nextSession

      return nextSession
    })
  }, [nextCardQuery.data, shouldRecoverCard, summaryQuery.data])

  const dismissCard = useCallback(
    (card: HomeFeedCard) => {
      if (markCardSeen.isPending || !session.isInitialized || session.currentCard?.id !== card.id) {
        return
      }

      const uniqueEventIds = [...new Set(card.feedEventIds)]
      const previousSession = session
      const optimisticSummary = getNextSummaryAfterDismiss(
        {
          totalCount: session.remainingCount,
          leadCount: session.leadCount,
          assetCount: session.assetCount,
        },
        card
      )
      const optimisticSession: FeedStackSession = {
        ...session,
        remainingCount: optimisticSummary.totalCount,
        leadCount: optimisticSummary.leadCount,
        assetCount: optimisticSummary.assetCount,
        currentCard: null,
        hasPagedThroughStack: true,
      }

      setSession(optimisticSession)
      persistedFeedStackSession = optimisticSession

      markCardSeen.mutate(
        {
          cutoffAt: session.cutoffAt,
          eventIds: uniqueEventIds,
        },
        {
          onSuccess: (result) => {
            setSession((currentSession) => {
              const nextSession = syncSessionWithServerState(
                {
                  ...currentSession,
                  hasPagedThroughStack: true,
                },
                result.summary,
                result.card
              )

              persistedFeedStackSession = nextSession

              return nextSession
            })
          },
          onError: () => {
            setSession(previousSession)
            persistedFeedStackSession = previousSession
          },
        }
      )
    },
    [markCardSeen, session]
  )

  return {
    currentCard: session.currentCard,
    remainingCount: session.remainingCount,
    totalCount: session.totalCount,
    currentCardIndex:
      session.totalCount > 0 ? Math.max(0, session.totalCount - session.remainingCount) : 0,
    leadCount: session.leadCount,
    assetCount: session.assetCount,
    hasPagedThroughStack: session.hasPagedThroughStack,
    isLoading:
      !session.isInitialized &&
      (summaryQuery.isLoading ||
        nextCardQuery.isLoading ||
        summaryQuery.isFetching ||
        nextCardQuery.isFetching),
    isAdvancing:
      session.isInitialized && session.currentCard === null && session.remainingCount > 0,
    dismissCard,
  }
}
